import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronDown, ChevronUp, Upload, Plus, X, Trash2, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { FacilityLocationForm } from '../address-book/FacilityLocationForm';
import { CustomerForm } from '../address-book/CustomerForm';

interface FreightItem {
  id: string;
  description: string;
  quantity: string;
  quantity_unit: string;
  weight: string;
  weight_unit: string;
  declared_value: string;
}

interface Charge {
  id: string;
  charge_type: string;
  charge_name: string;
  rate: string;
  amount: number;
}

export function CreateLoadPageComprehensive() {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  
  // Accordion states
  const [routeExpanded, setRouteExpanded] = useState(true);
  const [freightExpanded, setFreightExpanded] = useState(true);
  const [billingExpanded, setBillingExpanded] = useState(true);
  
  // Search states
  const [shipperSearch, setShipperSearch] = useState('');
  const [consigneeSearch, setConsigneeSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [factoringSearch, setFactoringSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [facilityResults, setFacilityResults] = useState<any[]>([]);
  const [customerResults, setCustomerResults] = useState<any[]>([]);
  const [factoringResults, setFactoringResults] = useState<any[]>([]);
  const [userResults, setUserResults] = useState<any[]>([]);
  const [showShipperDropdown, setShowShipperDropdown] = useState(false);
  const [showConsigneeDropdown, setShowConsigneeDropdown] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showFactoringDropdown, setShowFactoringDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  // Modal states
  const [showCreateFacilityModal, setShowCreateFacilityModal] = useState(false);
  const [facilityModalType, setFacilityModalType] = useState<'shipper' | 'consignee'>('shipper');
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  
  // Document upload states
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  
  // Basic Info
  const [formData, setFormData] = useState({
    rate_confirmation_number: '',
    booked_by: '',
    bol_numbers: '',
    load_number: '',
    customer_id: '',
    commodity: '',
    weight: '',
    total_rate: '',
    total_miles: '',
  });

  // Shipper
  const [shipper, setShipper] = useState({
    facility_name: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    date: '',
    time_from: '',
    time_to: '',
    contact_person: '',
    phone_number: '',
    phone_extension: '',
    instructions: '',
  });

  // Intermediate Stops
  const [intermediateStops, setIntermediateStops] = useState<Array<{
    id: string;
    stop_type: string;
    facility_name: string;
    street: string;
    city: string;
    state: string;
    zip_code: string;
    date: string;
    time_from: string;
    time_to: string;
    contact_person: string;
    phone_number: string;
    phone_extension: string;
    instructions: string;
  }>>([]);

  // Consignee
  const [consignee, setConsignee] = useState({
    facility_name: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    date: '',
    time_from: '',
    time_to: '',
    contact_person: '',
    phone_number: '',
    phone_extension: '',
    instructions: '',
  });

  // Freight Items
  const [freightItems, setFreightItems] = useState<FreightItem[]>([
    {
      id: '1',
      description: '',
      quantity: '',
      quantity_unit: 'Pallet(s)',
      weight: '',
      weight_unit: 'lbs',
      declared_value: '',
    },
  ]);

  // Billing
  const [billing, setBilling] = useState({
    customer_name: '',
    contact_info: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    email: '',
    phone_number: '',
    phone_extension: '',
    factoring_company: '',
    factoring_fee_percentage: '',
    transfer_documents: true,
    send_to_customer: true,
    transfer_to_factoring: true,
  });

  // Charges
  const [charges, setCharges] = useState<Charge[]>([
    { id: '1', charge_type: 'hauling', charge_name: 'Hauling Fee', rate: '', amount: 0 },
    { id: '2', charge_type: 'payee_hauling', charge_name: 'Payee Hauling Fee', rate: '', amount: 0 },
    { id: '3', charge_type: 'fuel_surcharge', charge_name: 'Fuel Surcharge', rate: '', amount: 0 },
  ]);

  // Search for facilities
  useEffect(() => {
    if (shipperSearch.length > 1) {
      searchFacilities(shipperSearch);
    } else {
      setFacilityResults([]);
    }
  }, [shipperSearch]);

  useEffect(() => {
    if (consigneeSearch.length > 1) {
      searchFacilities(consigneeSearch);
    }
  }, [consigneeSearch]);

  // Search for customers
  useEffect(() => {
    if (customerSearch.length > 1) {
      searchCustomers(customerSearch);
    } else {
      setCustomerResults([]);
    }
  }, [customerSearch]);

  // Search for factoring companies
  useEffect(() => {
    if (factoringSearch.length > 1) {
      searchFactoringCompanies(factoringSearch);
    } else {
      setFactoringResults([]);
    }
  }, [factoringSearch]);

  // Search for users
  useEffect(() => {
    if (userSearch.length > 1) {
      searchUsers(userSearch);
    } else {
      setUserResults([]);
    }
  }, [userSearch]);

  // Load current user on mount and generate load number
  useEffect(() => {
    loadCurrentUser();
    
    // Auto-generate unique load number (always generate new one)
    const timestamp = Date.now();
    const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const loadNumber = `LD-${timestamp}-${randomSuffix}`;
    setFormData(prev => ({ ...prev, load_number: loadNumber }));
  }, []);

  async function searchFacilities(query: string) {
    try {
      const { data, error } = await supabase
        .from('facility_locations')
        .select('*')
        .or(`name.ilike.%${query}%,city.ilike.%${query}%,state.ilike.%${query}%`)
        .limit(10);
      
      if (error) throw error;
      setFacilityResults(data || []);
    } catch (error) {
      console.error('Error searching facilities:', error);
    }
  }

  async function searchCustomers(query: string) {
    try {
      const { data, error} = await supabase
        .from('customers')
        .select('*')
        .or(`name.ilike.%${query}%,customer_number.ilike.%${query}%`)
        .limit(10);
      
      if (error) throw error;
      setCustomerResults(data || []);
    } catch (error) {
      console.error('Error searching customers:', error);
    }
  }

  async function searchFactoringCompanies(query: string) {
    try {
      const { data, error } = await supabase
        .from('factoring_companies')
        .select('*')
        .or(`name.ilike.%${query}%,company_id.ilike.%${query}%`)
        .limit(10);
      
      if (error) throw error;
      console.log('Factoring search results:', data);
      setFactoringResults(data || []);
    } catch (error) {
      console.error('Error searching factoring companies:', error);
    }
  }

  async function searchUsers(query: string) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, role')
        .or(`full_name.ilike.%${query}%,email.ilike.%${query}%`)
        .eq('is_active', true)
        .limit(10);
      
      if (error) throw error;
      console.log('User search results:', data);
      setUserResults(data || []);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  }

  async function loadCurrentUser() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userData } = await supabase
          .from('profiles')
          .select('id, email, full_name, role')
          .eq('id', user.id)
          .single();
        
        if (userData) {
          setCurrentUser(userData);
          const displayName = userData.full_name || userData.email;
          setFormData(prev => ({ ...prev, booked_by: displayName }));
          setUserSearch(displayName);
          console.log('Current user loaded:', displayName);
        }
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  }

  function selectShipperFacility(facility: any) {
    setShipper({
      ...shipper,
      facility_name: facility.name,
      street: facility.address || facility.street || '',
      city: facility.city || '',
      state: facility.state || '',
      zip_code: facility.zip || '',
      contact_person: facility.contact_name || '',
      phone_number: facility.phone || '',
      phone_extension: facility.contact_phone_ext || '',
      instructions: facility.stop_instructions || facility.notes || '',
    });
    setShipperSearch(facility.name);
    setShowShipperDropdown(false);
  }

  function selectConsigneeFacility(facility: any) {
    setConsignee({
      ...consignee,
      facility_name: facility.name,
      street: facility.address || facility.street || '',
      city: facility.city || '',
      state: facility.state || '',
      zip_code: facility.zip || '',
      contact_person: facility.contact_name || '',
      phone_number: facility.phone || '',
      phone_extension: facility.contact_phone_ext || '',
      instructions: facility.stop_instructions || facility.notes || '',
    });
    setConsigneeSearch(facility.name);
    setShowConsigneeDropdown(false);
  }

  function selectCustomer(customer: any) {
    setBilling({
      ...billing,
      customer_name: customer.name,
      contact_info: customer.contact_name || '',
      street: customer.billing_street || '',
      city: customer.billing_city || '',
      state: customer.billing_state || '',
      zip_code: customer.billing_zip || '',
      email: customer.billing_email || customer.contact_email || '',
      phone_number: customer.billing_phone || customer.contact_phone || '',
      phone_extension: customer.billing_phone_ext || customer.contact_phone_ext || '',
      factoring_company: customer.factoring_company || '',
      transfer_documents: customer.transfer_documents ?? true,
      send_to_customer: customer.email_notified ?? true,
      transfer_to_factoring: customer.transfer_to_factoring ?? true,
    });
    setFormData({ ...formData, customer_id: customer.id });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    
    // If customer has a factoring company, set it in the search
    if (customer.factoring_company) {
      setFactoringSearch(customer.factoring_company);
    }
  }

  function selectFactoringCompany(company: any) {
    setBilling({
      ...billing,
      factoring_company: company.name,
      factoring_fee_percentage: company.load_factoring_fee_percentage?.toString() || billing.factoring_fee_percentage,
    });
    setFactoringSearch(company.name);
    setShowFactoringDropdown(false);
  }

  function selectUser(user: any) {
    const displayName = user.full_name || user.email;
    setFormData({ ...formData, booked_by: displayName });
    setUserSearch(displayName);
    setShowUserDropdown(false);
  }

  function openCreateFacilityModal(type: 'shipper' | 'consignee') {
    setFacilityModalType(type);
    setShowCreateFacilityModal(true);
  }

  async function handleFacilityCreated() {
    // Reload facilities to get the newly created one
    setShowCreateFacilityModal(false);
    toast.success('Facility created! Please search for it to select.');
  }

  async function handleCustomerCreated() {
    // Reload customers to get the newly created one
    setShowCreateCustomerModal(false);
    toast.success('Customer created! Please search for it to select.');
  }

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      setUploadedDocuments(prev => [...prev, ...fileArray]);
      toast.success(`${fileArray.length} file(s) selected`);
    }
  }

  function removeDocument(index: number) {
    setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
  }

  async function uploadDocumentsToLoad(_loadId: string, loadNumber: string) {
    if (uploadedDocuments.length === 0) return;

    setUploading(true);
    try {
      // First, ensure LOADS parent folder exists (use maybeSingle to avoid 406 error)
      const { data: existingLoadsFolder, error: loadsFolderQueryError } = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', 'LOADS')
        .is('parent_folder_id', null)
        .maybeSingle();

      if (loadsFolderQueryError) throw loadsFolderQueryError;

      let loadsFolderId: string;
      
      if (!existingLoadsFolder) {
        const { data: newLoadsFolder, error: loadsFolderError } = await supabase
          .from('document_folders')
          .insert([{ name: 'LOADS', parent_folder_id: null }])
          .select()
          .single();
        
        if (loadsFolderError || !newLoadsFolder) throw loadsFolderError;
        loadsFolderId = newLoadsFolder.id;
      } else {
        loadsFolderId = existingLoadsFolder.id;
      }

      // Create or get load-specific folder (use maybeSingle to avoid 406 error)
      const { data: existingLoadFolder, error: loadFolderQueryError } = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', loadNumber)
        .eq('parent_folder_id', loadsFolderId)
        .maybeSingle();

      if (loadFolderQueryError) throw loadFolderQueryError;

      let loadFolderId: string;

      if (!existingLoadFolder) {
        const { data: newLoadFolder, error: loadFolderError } = await supabase
          .from('document_folders')
          .insert([{ 
            name: loadNumber, 
            parent_folder_id: loadsFolderId,
            load_id: _loadId  // Link folder to load for easy retrieval
          }])
          .select()
          .single();
        
        if (loadFolderError || !newLoadFolder) throw loadFolderError;
        loadFolderId = newLoadFolder.id;
      } else {
        loadFolderId = existingLoadFolder.id;
      }

      // Upload each document
      for (const file of uploadedDocuments) {
        // Sanitize filename: replace spaces and special characters
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `${Date.now()}_${sanitizedName}`;
        const filePath = `LOADS/${loadNumber}/${fileName}`;
        
        // Upload file to Supabase Storage (use 'company-documents' bucket)
        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Storage upload error:', uploadError);
          throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
        }

        // Create document record in database
        const { error: dbError } = await supabase
          .from('documents')
          .insert([{
            name: file.name,
            file_path: filePath,
            file_size: file.size,
            file_type: file.type || 'application/octet-stream',
            mime_type: file.type,
            folder_id: loadFolderId,
          }]);

        if (dbError) throw dbError;
      }

      toast.success(`${uploadedDocuments.length} document(s) uploaded successfully!`);
    } catch (error: any) {
      console.error('Error uploading documents:', error);
      toast.error(error.message || 'Failed to upload documents');
    } finally {
      setUploading(false);
    }
  }

  // Generate load number on mount
  useEffect(() => {
    async function generateLoadNumber() {
      try {
        const { data, error } = await supabase.rpc('generate_load_number');
        if (error) throw error;
        if (data) {
          setFormData(prev => ({ ...prev, load_number: data }));
        }
      } catch (error) {
        console.error('Error generating load number:', error);
        toast.error('Failed to generate load number');
      }
    }
    generateLoadNumber();
  }, []);

  const addIntermediateStop = () => {
    setIntermediateStops([
      ...intermediateStops,
      {
        id: Date.now().toString(),
        stop_type: 'Pickup',
        facility_name: '',
        street: '',
        city: '',
        state: '',
        zip_code: '',
        date: '',
        time_from: '',
        time_to: '',
        contact_person: '',
        phone_number: '',
        phone_extension: '',
        instructions: '',
      },
    ]);
  };

  const removeIntermediateStop = (id: string) => {
    setIntermediateStops(intermediateStops.filter((stop) => stop.id !== id));
  };

  const updateIntermediateStop = (id: string, field: string, value: string) => {
    setIntermediateStops(
      intermediateStops.map((stop) =>
        stop.id === id ? { ...stop, [field]: value } : stop
      )
    );
  };

  const addFreightItem = () => {
    setFreightItems([
      ...freightItems,
      {
        id: Date.now().toString(),
        description: '',
        quantity: '',
        quantity_unit: 'Pallet(s)',
        weight: '',
        weight_unit: 'lbs',
        declared_value: '',
      },
    ]);
  };

  const removeFreightItem = (id: string) => {
    setFreightItems(freightItems.filter((item) => item.id !== id));
  };

  const updateFreightItem = (id: string, field: string, value: string) => {
    setFreightItems(
      freightItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const addAccessorialFee = () => {
    setCharges([
      ...charges,
      {
        id: Date.now().toString(),
        charge_type: 'accessorial',
        charge_name: '',
        rate: '',
        amount: 0,
      },
    ]);
  };

  const removeCharge = (id: string) => {
    setCharges(charges.filter((charge) => charge.id !== id));
  };

  const updateCharge = (id: string, field: string, value: string) => {
    const updatedCharges = charges.map((charge) => {
      if (charge.id === id) {
        const updated = { ...charge, [field]: value };
        if (field === 'rate') {
          updated.amount = parseFloat(value) || 0;
        }
        return updated;
      }
      return charge;
    });
    setCharges(updatedCharges);
  };

  const calculateTotals = () => {
    const totalAmount = charges.reduce((sum, charge) => sum + charge.amount, 0);
    const grossAmount = totalAmount;
    const payeeGrossAmount = charges
      .filter((c) => c.charge_type === 'payee_hauling')
      .reduce((sum, charge) => sum + charge.amount, 0);

    return { totalAmount, grossAmount, payeeGrossAmount };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required date fields
      if (!shipper.date) {
        toast.error('Shipper date is required');
        setLoading(false);
        return;
      }
      if (!consignee.date) {
        toast.error('Consignee date is required');
        setLoading(false);
        return;
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // 1. Create load
      const { data: loadData, error: loadError } = await supabase
        .from('loads')
        .insert([
          {
            load_number: formData.load_number,
            customer_id: formData.customer_id || null,
            status: 'booked',
            commodity: formData.commodity,
            weight: parseInt(formData.weight),
            total_rate: parseFloat(formData.total_rate),
            total_miles: parseInt(formData.total_miles),
            pickup_date: shipper.date,
            delivery_date: consignee.date,
            pickup_city: shipper.city,
            pickup_state: shipper.state,
            delivery_city: consignee.city,
            delivery_state: consignee.state,
            rate_confirmation_number: formData.rate_confirmation_number,
            booked_by: formData.booked_by,
            bol_numbers: formData.bol_numbers,
            factoring_fee_percentage: parseFloat(billing.factoring_fee_percentage) || null,
            transfer_documents: billing.transfer_documents,
            send_to_customer: billing.send_to_customer,
            transfer_to_factoring: billing.transfer_to_factoring,
            created_by: user?.id,
          },
        ])
        .select()
        .single();

      if (loadError) throw loadError;
      const loadId = loadData.id;

      // 2. Create shipper stop
      const { error: shipperError } = await supabase
        .from('load_stops')
        .insert([
          {
            load_id: loadId,
            stop_type: 'shipper',
            stop_order: 1,
            facility_name: shipper.facility_name,
            street: shipper.street,
            city: shipper.city,
            state: shipper.state,
            zip_code: shipper.zip_code,
            date: shipper.date,
            time_from: shipper.time_from || null,
            time_to: shipper.time_to || null,
            contact_person: shipper.contact_person,
            phone_number: shipper.phone_number,
            phone_extension: shipper.phone_extension,
            instructions: shipper.instructions,
          },
        ]);

      if (shipperError) throw shipperError;

      // 3. Create intermediate stops
      if (intermediateStops.length > 0) {
        const intermediateStopsData = intermediateStops.map((stop, index) => ({
          load_id: loadId,
          stop_type: 'stop',
          stop_order: index + 2, // After shipper (1), before consignee
          facility_name: stop.facility_name,
          street: stop.street,
          city: stop.city,
          state: stop.state,
          zip_code: stop.zip_code,
          date: stop.date,
          time_from: stop.time_from || null,
          time_to: stop.time_to || null,
          contact_person: stop.contact_person,
          phone_number: stop.phone_number,
          phone_extension: stop.phone_extension,
          instructions: stop.instructions,
        }));

        const { error: intermediateStopsError } = await supabase
          .from('load_stops')
          .insert(intermediateStopsData);

        if (intermediateStopsError) throw intermediateStopsError;
      }

      // 4. Create consignee stop
      const consigneeStopOrder = 2 + intermediateStops.length; // After shipper and all intermediate stops
      const { error: consigneeError } = await supabase
        .from('load_stops')
        .insert([
          {
            load_id: loadId,
            stop_type: 'consignee',
            stop_order: consigneeStopOrder,
            facility_name: consignee.facility_name,
            street: consignee.street,
            city: consignee.city,
            state: consignee.state,
            zip_code: consignee.zip_code,
            date: consignee.date,
            time_from: consignee.time_from || null,
            time_to: consignee.time_to || null,
            contact_person: consignee.contact_person,
            phone_number: consignee.phone_number,
            phone_extension: consignee.phone_extension,
            instructions: consignee.instructions,
          },
        ]);

      if (consigneeError) throw consigneeError;

      // 4. Create freight items
      if (freightItems.length > 0 && freightItems[0].description) {
        const freightData = freightItems
          .filter((item) => item.description || item.quantity)
          .map((item) => ({
            load_id: loadId,
            description: item.description,
            quantity: parseInt(item.quantity) || null,
            quantity_unit: item.quantity_unit,
            weight: parseFloat(item.weight) || null,
            weight_unit: item.weight_unit,
            declared_value: parseFloat(item.declared_value) || null,
          }));

        // DISABLED: freight_items table doesn't exist
        // TODO: Implement freight items using correct table if needed
        // if (freightData.length > 0) {
        //   const { error: freightError } = await supabase
        //     .from('freight_items')
        //     .insert(freightData);
        //
        //   if (freightError) throw freightError;
        // }
      }

      // 5. Create charges
      const chargesData = charges
        .filter((charge) => charge.rate && parseFloat(charge.rate) > 0)
        .map((charge) => ({
          load_id: loadId,
          charge_type: charge.charge_type,
          charge_name: charge.charge_name,
          fee_method: 'flat_fee',
          rate: parseFloat(charge.rate),
          amount: charge.amount,
        }));

      if (chargesData.length > 0) {
        const { error: chargesError } = await supabase
          .from('load_charges')
          .insert(chargesData);

        if (chargesError) throw chargesError;
      }

      // 6. Create billing info
      if (billing.customer_name || billing.email) {
        const { error: billingError } = await supabase
          .from('load_billing_info')
          .insert([
            {
              load_id: loadId,
              customer_name: billing.customer_name,
              contact_info: billing.contact_info,
              street: billing.street,
              city: billing.city,
              state: billing.state,
              zip_code: billing.zip_code,
              email: billing.email,
              phone_number: billing.phone_number,
              phone_extension: billing.phone_extension,
            },
          ]);

        if (billingError) throw billingError;
      }

      // Upload documents if any
      if (uploadedDocuments.length > 0) {
        await uploadDocumentsToLoad(loadId, loadData.load_number);
      }

      toast.success('Load created successfully!');
      navigate('/loads');
    } catch (error) {
      console.error('Error creating load:', error);
      toast.error('Failed to create load');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate('/loads')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-slate-900">Create Load</h1>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/loads')}
                className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                onClick={handleSubmit}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Load'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Section */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Basic Information</h2>
              
              {/* Upload Rate Confirmation */}
              <div>
                <input
                  type="file"
                  id="rateConfirmationUpload"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label
                  htmlFor="rateConfirmationUpload"
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-sm text-slate-600 cursor-pointer"
                >
                  <Upload className="w-4 h-4" />
                  Upload Rate Confirmation
                </label>
              </div>
            </div>

            {/* Uploaded Documents List */}
            {uploadedDocuments.length > 0 && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Uploaded Documents ({uploadedDocuments.length})</h3>
                <div className="space-y-2">
                  {uploadedDocuments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border border-blue-200">
                      <div className="flex items-center gap-2">
                        <Upload className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-slate-900">{file.name}</span>
                        <span className="text-xs text-slate-500">({(file.size / 1024).toFixed(2)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(index)}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rate Confirmation No.*
                </label>
                <input
                  type="text"
                  value={formData.rate_confirmation_number}
                  onChange={(e) => setFormData({ ...formData, rate_confirmation_number: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Booked By*
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setShowUserDropdown(true);
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                    placeholder="Search user..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  {showUserDropdown && userSearch.length > 1 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {userResults.length > 0 ? (
                        userResults.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => selectUser(user)}
                            className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                          >
                            <div className="font-medium text-slate-900">
                              {user.full_name || user.email}
                            </div>
                            <div className="text-sm text-slate-600">
                              {user.email} • {user.role}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-slate-500 text-center">
                          No users found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {formData.booked_by && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-900">
                    Selected: {formData.booked_by}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Bill of Lading #
                </label>
                <input
                  type="text"
                  value={formData.bol_numbers}
                  onChange={(e) => setFormData({ ...formData, bol_numbers: e.target.value })}
                  placeholder="Multiple separated by comma"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">*Multiple separated by comma!</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Load Number*
                </label>
                <input
                  type="text"
                  value={formData.load_number}
                  onChange={(e) => setFormData({ ...formData, load_number: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Commodity*
                </label>
                <input
                  type="text"
                  value={formData.commodity}
                  onChange={(e) => setFormData({ ...formData, commodity: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Weight (lbs)*
                </label>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Total Rate ($)*
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.total_rate}
                  onChange={(e) => setFormData({ ...formData, total_rate: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Loaded Miles*
                </label>
                <input
                  type="number"
                  value={formData.total_miles}
                  onChange={(e) => setFormData({ ...formData, total_miles: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Loaded trip distance from the rate con. Empty/deadhead is added at dispatch.
                </p>
              </div>
            </div>
          </div>

          {/* Route Section - Collapsible */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setRouteExpanded(!routeExpanded)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <h2 className="text-lg font-semibold text-slate-900">Route</h2>
              {routeExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {routeExpanded && (
              <div className="p-6 border-t border-slate-200 space-y-8">
                {/* Shipper Details */}
                <div>
                  <h3 className="text-md font-semibold text-slate-900 mb-4">Shipper Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Search Facility Location
                      </label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input
                          type="text"
                          value={shipperSearch}
                          onChange={(e) => {
                            setShipperSearch(e.target.value);
                            setShowShipperDropdown(true);
                          }}
                          onFocus={() => setShowShipperDropdown(true)}
                          placeholder="Search facility..."
                          className="w-full pl-10 pr-12 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => openCreateFacilityModal('shipper')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded transition-colors"
                          title="Create new facility location"
                        >
                          <Plus className="w-4 h-4 text-blue-600" />
                        </button>
                        {showShipperDropdown && shipperSearch.length > 1 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {facilityResults.length > 0 ? (
                              facilityResults.map((facility) => (
                                <button
                                  key={facility.id}
                                  type="button"
                                  onClick={() => selectShipperFacility(facility)}
                                  className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                                >
                                  <div className="font-medium text-slate-900">{facility.name}</div>
                                  <div className="text-sm text-slate-600">
                                    {facility.city}, {facility.state} {facility.zip}
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-slate-500 text-center">
                                No facilities found. Click the + button to create a new one.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Street</label>
                      <input
                        type="text"
                        value={shipper.street}
                        onChange={(e) => setShipper({ ...shipper, street: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">City*</label>
                      <input
                        type="text"
                        value={shipper.city}
                        onChange={(e) => setShipper({ ...shipper, city: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">State*</label>
                      <input
                        type="text"
                        value={shipper.state}
                        onChange={(e) => setShipper({ ...shipper, state: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Zip Code*</label>
                      <input
                        type="text"
                        value={shipper.zip_code}
                        onChange={(e) => setShipper({ ...shipper, zip_code: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Date*</label>
                      <input
                        type="date"
                        value={shipper.date}
                        onChange={(e) => setShipper({ ...shipper, date: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Time From*</label>
                      <input
                        type="time"
                        value={shipper.time_from}
                        onChange={(e) => setShipper({ ...shipper, time_from: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Time To</label>
                      <input
                        type="time"
                        value={shipper.time_to}
                        onChange={(e) => setShipper({ ...shipper, time_to: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>
                      <input
                        type="text"
                        value={shipper.contact_person}
                        onChange={(e) => setShipper({ ...shipper, contact_person: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          value={shipper.phone_number}
                          onChange={(e) => setShipper({ ...shipper, phone_number: e.target.value })}
                          className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          value={shipper.phone_extension}
                          onChange={(e) => setShipper({ ...shipper, phone_extension: e.target.value })}
                          placeholder="ext"
                          className="w-20 px-2 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="lg:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Comment/Instructions</label>
                      <textarea
                        value={shipper.instructions}
                        onChange={(e) => setShipper({ ...shipper, instructions: e.target.value })}
                        maxLength={2000}
                        rows={3}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">Max length: 2000 characters!</p>
                    </div>
                  </div>
                </div>

                {/* Add Stop Button */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={addIntermediateStop}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                  >
                    Add Stop
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Intermediate Stops */}
                {intermediateStops.map((stop, index) => (
                  <div key={stop.id} className="relative">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-md font-semibold text-slate-900">Stop #{index + 1}</h3>
                      <button
                        type="button"
                        onClick={() => removeIntermediateStop(stop.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove Stop
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-3">
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Search Facility Location
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={stop.facility_name}
                            onChange={(e) => updateIntermediateStop(stop.id, 'facility_name', e.target.value)}
                            placeholder="Search facility..."
                            className="w-full px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                          >
                            <Plus className="w-4 h-4 text-blue-600" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Stop Type*</label>
                        <select
                          value={stop.stop_type}
                          onChange={(e) => updateIntermediateStop(stop.id, 'stop_type', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="Pickup">Pickup</option>
                          <option value="Drop-off">Drop-off</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Street</label>
                        <input
                          type="text"
                          value={stop.street}
                          onChange={(e) => updateIntermediateStop(stop.id, 'street', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">City*</label>
                        <input
                          type="text"
                          value={stop.city}
                          onChange={(e) => updateIntermediateStop(stop.id, 'city', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">State*</label>
                        <input
                          type="text"
                          value={stop.state}
                          onChange={(e) => updateIntermediateStop(stop.id, 'state', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Zip Code*</label>
                        <input
                          type="text"
                          value={stop.zip_code}
                          onChange={(e) => updateIntermediateStop(stop.id, 'zip_code', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Date*</label>
                        <input
                          type="date"
                          value={stop.date}
                          onChange={(e) => updateIntermediateStop(stop.id, 'date', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Time From*</label>
                        <input
                          type="time"
                          value={stop.time_from}
                          onChange={(e) => updateIntermediateStop(stop.id, 'time_from', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Time To</label>
                        <input
                          type="time"
                          value={stop.time_to}
                          onChange={(e) => updateIntermediateStop(stop.id, 'time_to', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>
                        <input
                          type="text"
                          value={stop.contact_person}
                          onChange={(e) => updateIntermediateStop(stop.id, 'contact_person', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                        <div className="flex gap-2">
                          <input
                            type="tel"
                            value={stop.phone_number}
                            onChange={(e) => updateIntermediateStop(stop.id, 'phone_number', e.target.value)}
                            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <input
                            type="text"
                            value={stop.phone_extension}
                            onChange={(e) => updateIntermediateStop(stop.id, 'phone_extension', e.target.value)}
                            placeholder="ext"
                            className="w-20 px-2 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="lg:col-span-3">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Comment/Instructions</label>
                        <textarea
                          value={stop.instructions}
                          onChange={(e) => updateIntermediateStop(stop.id, 'instructions', e.target.value)}
                          maxLength={2000}
                          rows={3}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="text-xs text-slate-500 mt-1">Max length: 2000 characters!</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Consignee Details */}
                <div>
                  <h3 className="text-md font-semibold text-slate-900 mb-4">Consignee Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Search Facility Location
                      </label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input
                          type="text"
                          value={consigneeSearch}
                          onChange={(e) => {
                            setConsigneeSearch(e.target.value);
                            setShowConsigneeDropdown(true);
                          }}
                          onFocus={() => setShowConsigneeDropdown(true)}
                          placeholder="Search facility..."
                          className="w-full pl-10 pr-12 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => openCreateFacilityModal('consignee')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded transition-colors"
                          title="Create new facility location"
                        >
                          <Plus className="w-4 h-4 text-blue-600" />
                        </button>
                        {showConsigneeDropdown && consigneeSearch.length > 1 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {facilityResults.length > 0 ? (
                              facilityResults.map((facility) => (
                                <button
                                  key={facility.id}
                                  type="button"
                                  onClick={() => selectConsigneeFacility(facility)}
                                  className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                                >
                                  <div className="font-medium text-slate-900">{facility.name}</div>
                                  <div className="text-sm text-slate-600">
                                    {facility.city}, {facility.state} {facility.zip}
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-slate-500 text-center">
                                No facilities found. Click the + button to create a new one.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Street</label>
                      <input
                        type="text"
                        value={consignee.street}
                        onChange={(e) => setConsignee({ ...consignee, street: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">City*</label>
                      <input
                        type="text"
                        value={consignee.city}
                        onChange={(e) => setConsignee({ ...consignee, city: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">State*</label>
                      <input
                        type="text"
                        value={consignee.state}
                        onChange={(e) => setConsignee({ ...consignee, state: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Zip Code*</label>
                      <input
                        type="text"
                        value={consignee.zip_code}
                        onChange={(e) => setConsignee({ ...consignee, zip_code: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Date*</label>
                      <input
                        type="date"
                        value={consignee.date}
                        onChange={(e) => setConsignee({ ...consignee, date: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Time From*</label>
                      <input
                        type="time"
                        value={consignee.time_from}
                        onChange={(e) => setConsignee({ ...consignee, time_from: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Time To</label>
                      <input
                        type="time"
                        value={consignee.time_to}
                        onChange={(e) => setConsignee({ ...consignee, time_to: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>
                      <input
                        type="text"
                        value={consignee.contact_person}
                        onChange={(e) => setConsignee({ ...consignee, contact_person: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          value={consignee.phone_number}
                          onChange={(e) => setConsignee({ ...consignee, phone_number: e.target.value })}
                          className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          value={consignee.phone_extension}
                          onChange={(e) => setConsignee({ ...consignee, phone_extension: e.target.value })}
                          placeholder="ext"
                          className="w-20 px-2 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="lg:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Comment/Instructions</label>
                      <textarea
                        value={consignee.instructions}
                        onChange={(e) => setConsignee({ ...consignee, instructions: e.target.value })}
                        maxLength={2000}
                        rows={3}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">Max length: 2000 characters!</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Freight Section - Collapsible */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setFreightExpanded(!freightExpanded)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <h2 className="text-lg font-semibold text-slate-900">Freight</h2>
              {freightExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {freightExpanded && (
              <div className="p-6 border-t border-slate-200">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left text-sm font-semibold text-slate-700 pb-3">Description</th>
                        <th className="text-left text-sm font-semibold text-slate-700 pb-3">Quantity</th>
                        <th className="text-left text-sm font-semibold text-slate-700 pb-3">Weight</th>
                        <th className="text-left text-sm font-semibold text-slate-700 pb-3">Declared Value</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {freightItems.map((item) => (
                        <tr key={item.id} className="border-b border-slate-100">
                          <td className="py-3">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateFreightItem(item.id, 'description', e.target.value)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                          <td className="py-3">
                            <div className="flex gap-2">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateFreightItem(item.id, 'quantity', e.target.value)}
                                className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <select
                                value={item.quantity_unit}
                                onChange={(e) => updateFreightItem(item.id, 'quantity_unit', e.target.value)}
                                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option>Pallet(s)</option>
                                <option>Piece(s)</option>
                                <option>Crate(s)</option>
                                <option>Box(es)</option>
                                <option>Bundle(s)</option>
                                <option>Unit(s)</option>
                              </select>
                            </div>
                          </td>
                          <td className="py-3">
                            <div className="flex gap-2">
                              <input
                                type="number"
                                value={item.weight}
                                onChange={(e) => updateFreightItem(item.id, 'weight', e.target.value)}
                                className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <select
                                value={item.weight_unit}
                                onChange={(e) => updateFreightItem(item.id, 'weight_unit', e.target.value)}
                                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option>lbs</option>
                                <option>tons</option>
                              </select>
                            </div>
                          </td>
                          <td className="py-3">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                              <input
                                type="number"
                                step="0.01"
                                value={item.declared_value}
                                onChange={(e) => updateFreightItem(item.id, 'declared_value', e.target.value)}
                                className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </td>
                          <td className="py-3">
                            {freightItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeFreightItem(item.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  onClick={addFreightItem}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add New Line
                </button>
              </div>
            )}
          </div>

          {/* Billing Section - Collapsible */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setBillingExpanded(!billingExpanded)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <h2 className="text-lg font-semibold text-slate-900">Billing</h2>
              {billingExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {billingExpanded && (
              <div className="p-6 border-t border-slate-200 space-y-6">
                {/* Customer Information */}
                <div>
                  <h3 className="text-md font-semibold text-slate-900 mb-4">Customer Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Search Customer*
                      </label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input
                          type="text"
                          value={customerSearch}
                          onChange={(e) => {
                            setCustomerSearch(e.target.value);
                            setShowCustomerDropdown(true);
                          }}
                          onFocus={() => setShowCustomerDropdown(true)}
                          placeholder="Search by name or customer number..."
                          className="w-full pl-10 pr-12 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowCreateCustomerModal(true)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded transition-colors"
                          title="Create new customer"
                        >
                          <Plus className="w-4 h-4 text-blue-600" />
                        </button>
                        {showCustomerDropdown && customerSearch.length > 1 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {customerResults.length > 0 ? (
                              customerResults.map((customer) => (
                                <button
                                  key={customer.id}
                                  type="button"
                                  onClick={() => selectCustomer(customer)}
                                  className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                                >
                                  <div className="font-medium text-slate-900">{customer.name}</div>
                                  <div className="text-sm text-slate-600">
                                    Customer #{customer.customer_number}
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-slate-500 text-center">
                                No customers found.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {billing.customer_name && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="text-sm font-medium text-blue-900">Selected: {billing.customer_name}</div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Contact Info</label>
                      <input
                        type="text"
                        value={billing.contact_info}
                        onChange={(e) => setBilling({ ...billing, contact_info: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Street</label>
                      <input
                        type="text"
                        value={billing.street}
                        onChange={(e) => setBilling({ ...billing, street: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">City*</label>
                      <input
                        type="text"
                        value={billing.city}
                        onChange={(e) => setBilling({ ...billing, city: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">State*</label>
                      <input
                        type="text"
                        value={billing.state}
                        onChange={(e) => setBilling({ ...billing, state: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Zip Code*</label>
                      <input
                        type="text"
                        value={billing.zip_code}
                        onChange={(e) => setBilling({ ...billing, zip_code: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email*</label>
                      <input
                        type="email"
                        value={billing.email}
                        onChange={(e) => setBilling({ ...billing, email: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          value={billing.phone_number}
                          onChange={(e) => setBilling({ ...billing, phone_number: e.target.value })}
                          className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          value={billing.phone_extension}
                          onChange={(e) => setBilling({ ...billing, phone_extension: e.target.value })}
                          placeholder="ext"
                          className="w-20 px-2 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Factoring Company*</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input
                          type="text"
                          value={factoringSearch}
                          onChange={(e) => {
                            setFactoringSearch(e.target.value);
                            setShowFactoringDropdown(true);
                          }}
                          onFocus={() => setShowFactoringDropdown(true)}
                          placeholder="Search factoring company..."
                          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                        {showFactoringDropdown && factoringSearch.length > 1 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {factoringResults.length > 0 ? (
                              factoringResults.map((company) => (
                                <button
                                  key={company.id}
                                  type="button"
                                  onClick={() => selectFactoringCompany(company)}
                                  className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                                >
                                  <div className="font-medium text-slate-900">{company.name}</div>
                                  <div className="text-sm text-slate-600">
                                    ID: {company.company_id || 'N/A'} • Fee: {company.load_factoring_fee_percentage || 0}%
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-slate-500 text-center">
                                No factoring companies found.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {billing.factoring_company && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="text-sm font-medium text-blue-900">Selected: {billing.factoring_company}</div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Load Factoring Fee (%)*</label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.01"
                          value={billing.factoring_fee_percentage}
                          onChange={(e) => setBilling({ ...billing, factoring_fee_percentage: e.target.value })}
                          className="w-full pl-3 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                      </div>
                    </div>

                    <div className="lg:col-span-3 space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={billing.transfer_documents}
                          onChange={(e) => setBilling({ ...billing, transfer_documents: e.target.checked })}
                          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">Transfer Documents</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={billing.send_to_customer}
                          onChange={(e) => setBilling({ ...billing, send_to_customer: e.target.checked })}
                          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">Send documents to customer</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={billing.transfer_to_factoring}
                          onChange={(e) => setBilling({ ...billing, transfer_to_factoring: e.target.checked })}
                          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">Transfer documents to factoring</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Charges */}
                <div>
                  <h3 className="text-md font-semibold text-slate-900 mb-4">Charges</h3>
                  <div className="space-y-3">
                    {charges.map((charge) => (
                      <div key={charge.id} className="flex items-center gap-4">
                        <div className="flex-1">
                          {charge.charge_type === 'accessorial' ? (
                            <select
                              value={charge.charge_name}
                              onChange={(e) => updateCharge(charge.id, 'charge_name', e.target.value)}
                              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select Accessorial Fee</option>
                              <option>Detention</option>
                              <option>Driver Assistance</option>
                              <option>Extra fee</option>
                              <option>Layover</option>
                              <option>Lumper</option>
                              <option>Tarp</option>
                              <option>TONU</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={charge.charge_name}
                              readOnly
                              className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-50"
                            />
                          )}
                        </div>
                        <div className="w-40">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={charge.rate}
                              onChange={(e) => updateCharge(charge.id, 'rate', e.target.value)}
                              placeholder="0.00"
                              className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        <div className="w-32 text-right font-semibold text-slate-900">
                          $ {charge.amount.toFixed(2)}
                        </div>
                        {charge.charge_type === 'accessorial' && (
                          <button
                            type="button"
                            onClick={() => removeCharge(charge.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addAccessorialFee}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add Accessorial Fee
                  </button>

                  {/* Totals */}
                  <div className="mt-6 pt-6 border-t border-slate-200 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Total Amount</span>
                      <span className="font-bold text-slate-900">$ {totals.totalAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Gross Amount</span>
                      <span className="font-bold text-slate-900">$ {totals.grossAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Payee Gross Amount</span>
                      <span className="font-bold text-slate-900">$ {totals.payeeGrossAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Create Facility Modal - Using Address Book Form */}
      {showCreateFacilityModal && (
        <FacilityLocationForm
          onClose={() => setShowCreateFacilityModal(false)}
          onSuccess={handleFacilityCreated}
        />
      )}

      {/* Create Customer Modal - Using Address Book Form */}
      {showCreateCustomerModal && (
        <CustomerForm
          onClose={() => setShowCreateCustomerModal(false)}
          onSuccess={handleCustomerCreated}
        />
      )}
    </div>
  );
}
